import React from 'react';
import { BreadcrumbGroup } from '@amzn/awsui-components-console';
import { ROUTES } from '../types/navigationTypes';
import { useNavigation } from '../hooks/useNavigation';
import { useStore } from 'src/stores/store';
import {
	createBaseBreadcrumbItem,
	createApplicationBreadcrumbItem,
	createOperationBreadcrumbItem,
	createAddServiceBreadcrumbItem,
	createAddOperationBreadcrumbItem,
	createExportToTaskBreadcrumbItem,
	needsApplicationContext,
	needsOperationContext,
} from '../utils/breadcrumbUtils';
import { handleRouteNavigation } from '../utils/routeHandlers';

const NavigationBreadcrumbs: React.FC = () => {
	const { navigate } = useNavigation();
	const { currentView, selectedApplication, selectedOperation } = useStore();

	const context = {
		currentView,
		selectedApplicationId: selectedApplication?.applicationId || null,
		selectedApplicationName: selectedApplication?.applicationName || null,
		selectedOperationId: selectedOperation?.operationId || null,
		selectedOperationName: selectedOperation?.operationName || null,
		selectedSupportedOperation: selectedOperation || undefined,
	};

	const viewActions = {
		navigateToApplications: () =>
			navigate(ROUTES.APPLICATIONS, '/applicationManagement'),
		navigateToApplicationDetails: () => {
			if (selectedApplication) {
				navigate(
					ROUTES.APPLICATION_DETAILS,
					`/applicationManagement/${selectedApplication.applicationId}`
				);
			}
		},
		navigateToOperationDetails: () => {
			if (selectedOperation && selectedApplication) {
				navigate(
					ROUTES.OPERATION_DETAILS,
					`/applicationManagement/${selectedApplication.applicationId}/${selectedOperation.operationId}`
				);
			}
		},
	};

	const creationActions = {
		...viewActions,
		navigateToAddApplication: () =>
			navigate(ROUTES.ADD_APPLICATION, '/applicationManagement/add'),
		navigateToAddOperation: () => {
			if (selectedApplication) {
				navigate(
					ROUTES.ADD_OPERATION,
					`/applicationManagement/${selectedApplication.applicationId}/add`
				);
			}
		},
		navigateToExportOperationToTask: () => {
			if (selectedOperation && selectedApplication) {
				navigate(
					ROUTES.EXPORT_OPERATION_TO_TASK,
					`/applicationManagement/${selectedApplication.applicationId}/${selectedOperation.operationId}/export`
				);
			}
		},
	};

	const getBreadcrumbItems = () => {
		const items = [createBaseBreadcrumbItem(viewActions)];

		if (currentView === ROUTES.ADD_APPLICATION) {
			items.push(createAddServiceBreadcrumbItem());

			return items;
		}

		if (
			selectedApplication?.applicationId &&
			needsApplicationContext(currentView)
		) {
			items.push(createApplicationBreadcrumbItem(context, viewActions));

			if (currentView === ROUTES.ADD_OPERATION) {
				items.push(
					createAddOperationBreadcrumbItem(selectedApplication.applicationId)
				);
			} else if (
				selectedOperation?.operationId &&
				needsOperationContext(currentView)
			) {
				items.push(createOperationBreadcrumbItem(context, viewActions));

				if (currentView === ROUTES.EXPORT_OPERATION_TO_TASK) {
					items.push(
						createExportToTaskBreadcrumbItem(
							selectedApplication.applicationId,
							selectedOperation.operationId
						)
					);
				}
			}
		}

		return items;
	};

	const handleBreadcrumbClick = (event: CustomEvent) => {
		event.preventDefault();
		const href = event.detail.href;

		handleRouteNavigation(href, creationActions, context);
	};

	return (
		<BreadcrumbGroup
			items={getBreadcrumbItems()}
			onFollow={handleBreadcrumbClick}
			ariaLabel="Navigation breadcrumbs"
		/>
	);
};

export default NavigationBreadcrumbs;
